import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent, type RefObject } from 'react'
import type { EliteConsentTextItem } from '../../types/misc.types'
import { openInBrowser } from '../../utils/appBridge'

const READ_MORE_LABEL = 'Read More'
const ELLIPSIS = '…'
const MAX_COLLAPSED_LINES = 2
const LINE_HEIGHT_TOLERANCE_PX = 1

type TruncationState = {
  shouldTruncate: boolean
  visibleText: string
}

type EliteConsentTextContentProps = {
  consentTextList?: EliteConsentTextItem[] | null
  className?: string
  readMoreExpanded?: boolean
  onReadMoreExpand?: (e: MouseEvent) => void
}

export function hasEliteConsentText(
  consentTextList?: EliteConsentTextItem[] | null,
  _consentText?: string | null
): boolean {
  return (consentTextList?.length ?? 0) > 0
}

export function getEliteConsentScrollSpacerClass(
  _consentText?: string | null,
  consentTextList?: EliteConsentTextItem[] | null
): string {
  return hasEliteConsentText(consentTextList) ? 'h-[220px]' : 'h-[140px]'
}

export function EliteConsentTextContent({
  consentTextList,
  className,
  readMoreExpanded: readMoreExpandedProp,
  onReadMoreExpand: onReadMoreExpandProp
}: EliteConsentTextContentProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [internalReadMoreExpanded, setInternalReadMoreExpanded] = useState(false)
  const readMoreExpanded = readMoreExpandedProp ?? internalReadMoreExpanded

  const items = useMemo(
    () => (consentTextList ?? []).filter((item) => Boolean(item?.consentText?.trim())),
    [consentTextList]
  )
  const combinedText = useMemo(() => joinConsentTextItems(items), [items])

  const [truncation, setTruncation] = useState<TruncationState>({
    shouldTruncate: false,
    visibleText: combinedText
  })

  const recalculateTruncation = useCallback(() => {
    const container = containerRef.current
    if (!container || readMoreExpanded || !combinedText.trim()) {
      setTruncation({ shouldTruncate: false, visibleText: combinedText })
      return
    }

    setTruncation(calculateConsentTruncation(container, combinedText, MAX_COLLAPSED_LINES))
  }, [combinedText, readMoreExpanded])

  useLayoutEffect(() => {
    recalculateTruncation()
  }, [recalculateTruncation])

  useResizeObserver(containerRef, recalculateTruncation)

  const handleReadMoreExpand = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (onReadMoreExpandProp) {
      onReadMoreExpandProp(event)
      return
    }
    setInternalReadMoreExpanded(true)
  }

  if (items.length === 0) return null

  const showCollapsedPreview = truncation.shouldTruncate && !readMoreExpanded

  return (
    <div ref={containerRef} className={className ? `relative ${className}` : 'relative'}>
      {showCollapsedPreview ? (
        <CollapsedConsentPreview
          visibleText={truncation.visibleText}
          onReadMoreExpand={handleReadMoreExpand}
        />
      ) : (
        <ExpandedConsentText items={items} />
      )}
    </div>
  )
}

function CollapsedConsentPreview({
  visibleText,
  onReadMoreExpand
}: {
  visibleText: string
  onReadMoreExpand: (event: MouseEvent) => void
}) {
  return (
    <>
      {visibleText}
      {ELLIPSIS}{' '}
      <div
        className='inline cursor-pointer underline whitespace-nowrap'
        onClick={onReadMoreExpand}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {READ_MORE_LABEL}
      </div>
    </>
  )
}

function ExpandedConsentText({ items }: { items: EliteConsentTextItem[] }) {
  return (
    <>
      {items.map((item, index) => {
        const text = item?.consentText ?? ''
        const url = item?.ctaUrl
        const separator = index > 0 ? ' ' : null

        if (url) {
          return (
            <span key={index}>
              {separator}
              <a
                href={url}
                className='relative z-10 inline cursor-pointer underline text-[#8E9393] active:text-[#8E9393] hover:text-[#8E9393]'
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  openInBrowser(url)
                }}
              >
                {text}
              </a>
            </span>
          )
        }

        return (
          <span key={index}>
            {separator}
            {text}
          </span>
        )
      })}
    </>
  )
}

function joinConsentTextItems(items: EliteConsentTextItem[]): string {
  return items.map((item) => item?.consentText ?? '').filter(Boolean).join(' ')
}

function useResizeObserver(targetRef: RefObject<HTMLElement | null>, onResize: () => void) {
  useEffect(() => {
    const target = targetRef.current
    if (!target || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => {
      onResize()
    })
    observer.observe(target)
    return () => observer.disconnect()
  }, [onResize, targetRef])
}

function calculateConsentTruncation(
  container: HTMLElement,
  fullText: string,
  maxLines: number
): TruncationState {
  const containerWidth = container.getBoundingClientRect().width
  if (!fullText.trim() || containerWidth <= 0) {
    return { shouldTruncate: false, visibleText: fullText }
  }

  const measureElement = createConsentMeasureElement(container)

  try {
    setFullMeasureContent(measureElement, fullText)
    if (getRenderedLineCount(measureElement) <= maxLines) {
      return { shouldTruncate: false, visibleText: fullText }
    }

    const visibleText = findLongestCollapsedPreview(fullText, (candidate) => {
      setCollapsedMeasureContent(measureElement, candidate)
      return getRenderedLineCount(measureElement) <= maxLines
    })

    if (!visibleText.trim()) {
      return { shouldTruncate: false, visibleText: fullText }
    }

    return { shouldTruncate: true, visibleText }
  } finally {
    measureElement.remove()
  }
}

function findLongestCollapsedPreview(
  fullText: string,
  fitsCollapsedPreview: (visibleText: string) => boolean
): string {
  let bestVisibleText = ''

  for (let endIndex = 1; endIndex <= fullText.length; endIndex++) {
    const candidate = fullText.slice(0, endIndex).trimEnd()
    if (!candidate || candidate.length <= bestVisibleText.length) continue

    if (fitsCollapsedPreview(candidate)) {
      bestVisibleText = candidate
    }
  }

  return bestVisibleText
}

function createConsentMeasureElement(container: HTMLElement): HTMLDivElement {
  const measureElement = document.createElement('div')
  measureElement.setAttribute('aria-hidden', 'true')
  measureElement.style.position = 'absolute'
  measureElement.style.visibility = 'hidden'
  measureElement.style.pointerEvents = 'none'
  measureElement.style.left = '0'
  measureElement.style.top = '0'
  measureElement.style.width = '100%'
  measureElement.style.height = 'auto'
  measureElement.style.zIndex = '-1'

  container.appendChild(measureElement)
  return measureElement
}

function getRenderedLineCount(element: HTMLElement): number {
  const range = document.createRange()
  range.selectNodeContents(element)

  const clientRects = Array.from(range.getClientRects()).filter(
    (rect) => rect.width > 0 && rect.height > 0
  )
  if (clientRects.length === 0) return 0

  const lineTops: number[] = []
  for (const rect of clientRects) {
    const top = Math.round(rect.top)
    const hasMatchingLine = lineTops.some((lineTop) => Math.abs(lineTop - top) <= LINE_HEIGHT_TOLERANCE_PX)
    if (!hasMatchingLine) lineTops.push(top)
  }

  return lineTops.length
}

function setFullMeasureContent(measureElement: HTMLDivElement, fullText: string) {
  measureElement.replaceChildren()
  measureElement.append(document.createTextNode(fullText))
}

function setCollapsedMeasureContent(measureElement: HTMLDivElement, visibleText: string) {
  measureElement.replaceChildren()
  measureElement.append(document.createTextNode(`${visibleText}${ELLIPSIS} `))

  const readMoreSpan = document.createElement('span')
  readMoreSpan.className = 'inline underline whitespace-nowrap'
  readMoreSpan.textContent = READ_MORE_LABEL
  measureElement.append(readMoreSpan)
}
